import React, { useState } from 'react';
import classNames from 'classnames';
import styles from './styles.module.scss';
import toggleStyles from 'src/components/Toggle/styles.module.scss';
import Flex from 'src/components/Flex';
import Card from 'src/components/Card';
import Icon from 'src/components/Icon';
import ConfirmationModal from 'src/components/ConfirmationModal';
import { IntegrationConfig } from 'src/api/surveyIntegration';
import { useUpdateSurveyIntegrationRouting } from 'src/queries/useSurveyIntegration';

interface RoutingToggleSectionProps {
    config: IntegrationConfig;
}

const DISABLE_CONFIRM_MESSAGE =
    'Are you sure? This will revert to the legacy survey pipeline. Previously migrated data remains in the new system and is not affected.';
const SUCCESS_MESSAGE = 'Routing updated. Takes effect on the next scheduled run.';

function errorMessage(error: unknown): string {
    return error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.';
}

function RoutingToggleSection({ config }: RoutingToggleSectionProps) {
    const [showDisableConfirm, setShowDisableConfirm] = useState(false);
    const updateRouting = useUpdateSurveyIntegrationRouting();

    const enabled = config.routingEnabled;

    const applyRouting = (nextEnabled: boolean) => {
        updateRouting.mutate(nextEnabled);
    };

    const handleToggle = () => {
        if (updateRouting.isLoading) return;
        if (enabled) {
            setShowDisableConfirm(true);
        } else {
            applyRouting(true);
        }
    };

    const handleConfirmDisable = () => {
        setShowDisableConfirm(false);
        applyRouting(false);
    };

    return (
        <Card className={styles.cardWrap} label="Survey Routing">
            <Flex direction="column" className={styles.sectionBody}>
                <Flex direction="row" align="center" className={styles.routingRow}>
                    <div
                        role="switch"
                        tabIndex={0}
                        aria-checked={enabled}
                        aria-label="Survey routing"
                        className={classNames(toggleStyles.container, { [toggleStyles.toggled]: enabled })}
                        onClick={handleToggle}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleToggle();
                            }
                        }}
                    >
                        <div
                            className={classNames(toggleStyles.toggleButton, { [toggleStyles.toggled]: enabled })}
                        />
                    </div>
                    <span className={styles.statusLabel}>{enabled ? 'On' : 'Off'}</span>
                    {updateRouting.isLoading && (
                        <Icon type="fa" name="spinner" spin className={styles.statusSpinner} />
                    )}
                </Flex>

                <div className={styles.routingDescription}>
                    {enabled
                        ? 'New patient surveys are created in the Graphium Surveys platform.'
                        : 'Legacy survey pipeline is used for new patient surveys.'}
                </div>
                <div className={styles.routingDescriptionMuted}>
                    When ON, new patient surveys are created in the Graphium Surveys platform. When OFF, the legacy
                    survey pipeline is used.
                </div>

                {updateRouting.isError && <div className={styles.error}>{errorMessage(updateRouting.error)}</div>}
                {updateRouting.isSuccess && <div className={styles.successMessage}>{SUCCESS_MESSAGE}</div>}
            </Flex>

            {showDisableConfirm && (
                <ConfirmationModal
                    headerTitle="Revert to legacy pipeline?"
                    title={DISABLE_CONFIRM_MESSAGE}
                    confirmButtonText="Revert to Legacy"
                    onConfirm={handleConfirmDisable}
                    onCancel={() => setShowDisableConfirm(false)}
                />
            )}
        </Card>
    );
}

export default RoutingToggleSection;
